// Aurora vector basemap selector.
//
// Picks the Google Maps cloud Map ID for the active app theme:
//   - VITE_GOOGLE_MAPS_MAP_ID_DARK  / VITE_GOOGLE_MAPS_MAP_ID_LIGHT — the Aurora
//     vector-styled Map IDs (created in the GCP console; injected at BUILD time
//     via Secret Manager / cloudbuild.yaml — an ops follow-up, NOT done in code).
//   - Falls back to LEGACY_MAP_ID when the per-theme var is absent, so the map
//     keeps working (theme-tinted via the `colorScheme` stopgap) until the Aurora
//     Map IDs exist. The moment the env vars are present at build time, every
//     <Map mapId={useAuroraMapId()}> upgrades to the Aurora basemap — no further
//     code change required.
//
// Theme is observed reactively off the `.light` class on <html> (the single
// source lib/theme.ts toggles), so the basemap re-selects its Map ID the instant
// the theme flips — matching the existing colorScheme observers in the map cards.
import React from 'react';
import { getInitialTheme, type ResolvedTheme } from './theme';

/** The pre-Aurora default Map ID; the colorScheme stopgap still theme-tints it. */
export const LEGACY_MAP_ID = 'bf51a910020fa25a';

function readResolvedTheme(): ResolvedTheme {
  if (typeof document === 'undefined') return getInitialTheme();
  return document.documentElement.classList.contains('light') ? 'light' : 'dark';
}

/**
 * Reactive resolved app theme (`'dark' | 'light'`), observed off the <html>
 * `.light` class. Shared so map cards can stop duplicating the observer.
 */
export function useResolvedTheme(): ResolvedTheme {
  const [theme, setTheme] = React.useState<ResolvedTheme>(readResolvedTheme);
  React.useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    const root = document.documentElement;
    const sync = () => setTheme(root.classList.contains('light') ? 'light' : 'dark');
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return theme;
}

function trimmedEnv(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

/**
 * Pure (non-hook) resolver — the Aurora Map ID for a known theme, or the legacy
 * default when its env var is unset. Safe to call in render or outside React.
 */
export function resolveAuroraMapId(theme: ResolvedTheme): string {
  const env = import.meta.env as Record<string, string | undefined>;
  const dark = trimmedEnv(env.VITE_GOOGLE_MAPS_MAP_ID_DARK);
  const light = trimmedEnv(env.VITE_GOOGLE_MAPS_MAP_ID_LIGHT);
  return (theme === 'light' ? light : dark) ?? LEGACY_MAP_ID;
}

/**
 * The Map ID to pass to `<Map mapId={...}>`. Aurora vector style per theme when
 * its env var is set; otherwise the legacy default. Re-evaluates on theme flip.
 */
export function useAuroraMapId(): string {
  return resolveAuroraMapId(useResolvedTheme());
}
